import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModalBodyClass } from '../../../modals/modal-body';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { ZiroFormsModule } from '../../../forms/ziro-forms.module';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { ZiroRadioConfig } from '../../../forms/fields/radio/ziro-radio.component';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import { TeamsAutoAttendantsGroupSearchResource } from '../../resources/teams-auto-attendants-group-search.resource';

type InclusionMode = 'ALL' | 'CUSTOM';
type ExclusionMode = 'NONE' | 'CUSTOM';

export interface DialScopeFormData {
  inclusionMode: InclusionMode;
  exclusionMode: ExclusionMode;
  inclusionGroups: ZiroSelectOption[];
  exclusionGroups: ZiroSelectOption[];
}

export interface DialScopeResult {
  inclusionMode: InclusionMode;
  inclusionGroups: ZiroSelectOption[];
  exclusionMode: ExclusionMode;
  exclusionGroups: ZiroSelectOption[];
}

@Component({
  selector: 'ziro-dial-scope-modal',
  standalone: true,
  imports: [CommonModule, ZiroFormsModule, TranslateModule],
  templateUrl: './dial-scope-modal.component.html',
  styleUrls: ['./dial-scope-modal.component.scss'],
  providers: [TeamsAutoAttendantsGroupSearchResource],
})
export class DialScopeModalComponent extends ModalBodyClass<DialScopeFormData> implements OnInit {
  private _modalComponent = inject(DetailedModalComponent<any>);
  private _autoAttendantsGroupSearchResource = inject(TeamsAutoAttendantsGroupSearchResource);
  private _tenantId: string;

  formConfig: ZiroFormConfig = {
    fields: {
      inclusionMode: {
        dataAutomation: 'dial-scope-inclusion-mode',
        componentConfig: new ZiroRadioConfig<InclusionMode>({
          hideLabel: true,
          buttons: [
            {
              value: 'ALL',
              label: 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.all_online_users',
            },
            {
              value: 'CUSTOM',
              label: 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.custom_user_group',
            },
          ],
        }),
      },
      inclusionGroups: {
        dataAutomation: 'dial-scope-inclusion-groups',
        componentConfig: new ZiroSelectConfig({
          isMultiSelect: true,
          minSearchLength: 3,
          asyncOptionsFn: (query: string) => this._searchInclusionGroups(query.trim()),
        }),
        required: () => this.formData.inclusionMode === 'CUSTOM',
        hidden: () => this.formData.inclusionMode !== 'CUSTOM',
        valExcluded: () => this.formData.inclusionMode !== 'CUSTOM',
      },
      exclusionMode: {
        dataAutomation: 'dial-scope-exclusion-mode',
        componentConfig: new ZiroRadioConfig<ExclusionMode>({
          hideLabel: true,
          buttons: [
            {
              value: 'NONE',
              label: 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.no_one',
            },
            {
              value: 'CUSTOM',
              label: 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.custom_user_group',
            },
          ],
        }),
      },
      exclusionGroups: {
        dataAutomation: 'dial-scope-exclusion-groups',
        componentConfig: new ZiroSelectConfig({
          isMultiSelect: true,
          minSearchLength: 3,
          asyncOptionsFn: (query: string) => this._searchExclusionGroups(query.trim()),
        }),
        required: () => this.formData.exclusionMode === 'CUSTOM',
        hidden: () => this.formData.exclusionMode !== 'CUSTOM',
        valExcluded: () => this.formData.exclusionMode !== 'CUSTOM',
      },
    },
  };

  ngOnInit(): void {
    const props = this._modalComponent.modalViewProperties;
    this._tenantId = props.tenantId;

    const inclusionMode: InclusionMode = props.inclusionMode ?? 'ALL';
    const exclusionMode: ExclusionMode = props.exclusionMode ?? 'NONE';
    const inclusionGroups: ZiroSelectOption[] = props.inclusionGroups ?? [];
    const exclusionGroups: ZiroSelectOption[] = props.exclusionGroups ?? [];

    this.entitySource.next({ inclusionMode, exclusionMode, inclusionGroups, exclusionGroups });
  }

  submit(): Observable<DialScopeResult> {
    return of({
      inclusionMode: this.formData.inclusionMode,
      inclusionGroups: this.formData.inclusionMode === 'CUSTOM' ? this.formData.inclusionGroups : [],
      exclusionMode: this.formData.exclusionMode,
      exclusionGroups: this.formData.exclusionMode === 'CUSTOM' ? this.formData.exclusionGroups : [],
    });
  }

  private _searchInclusionGroups = (query: string): Observable<ZiroSelectOption[]> => {
    return this._autoAttendantsGroupSearchResource.search(this._tenantId, query).pipe(
      map((groups) => {
        const excludedIds = new Set((this.formData.exclusionGroups ?? []).map((group) => group.value));
        return groups.map((group) => ({
          label: group.name,
          value: group.id,
          disabled: excludedIds.has(group.id),
          disabledTooltip: excludedIds.has(group.id)
            ? 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.already_excluded'
            : undefined,
        }));
      }),
    );
  };

  private _searchExclusionGroups = (query: string): Observable<ZiroSelectOption[]> => {
    return this._autoAttendantsGroupSearchResource.search(this._tenantId, query).pipe(
      map((groups) => {
        const includedIds = new Set((this.formData.inclusionGroups ?? []).map((group) => group.value));
        return groups.map((group) => ({
          label: group.name,
          value: group.id,
          disabled: includedIds.has(group.id),
          disabledTooltip: includedIds.has(group.id)
            ? 'tkey;admin.teams_voice_management.teams_auto_attendant.dial_scope.modal.already_included'
            : undefined,
        }));
      }),
    );
  };
}
